import React from 'react';
import { useTranslation } from 'react-i18next';
import Datagrid from '../../../../UI/Datagrid';
import ActionsDropdown from '../../../Dashboard/helpers/ActionsDropdown';
import {
  DisableModal,
  EnableModal,
} from '../../../Dashboard/helpers/SharedModal';
import Moment from 'moment';
import helper from '../../../../../utils/helper';
import { useHistory } from 'react-router-dom';
import {
  getAvailableGroups,
  setSensorsAtRest,
  setSensorsDisable,
  setSensorsEnable,
  setSensorsInOp,
} from '../../../../../api/dashboard';

const AGComponent = ({ ...props }) => {
  const refreshRate = helper.getSetting('ValueRefreshRateSec').value * 1000;
  const history = useHistory();
  const { t } = useTranslation('app');
  const [availableGroups, updateAvailableGroups] = React.useState([]);
  const [selectedGroups, setSelectedGroups] = React.useState([]);
  const intervalRef = React.useRef();
  const [didMount, setDidMount] = React.useState(false);

  function getData() {
    getAvailableGroups()
      .then((resp) => updateAvailableGroups(resp))
      .catch((err) => console.log('available groups err', err));
  }

  const getIntervalData = () => {
    const interval = setInterval(() => {
      getData();
    }, refreshRate);
    return interval;
  };

  React.useEffect(() => {
    getData();
    intervalRef.current = getIntervalData();
    setDidMount(true);
    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (selectedGroups.length) {
      clearInterval(intervalRef.current);
    }
    if (!selectedGroups.length && didMount) {
      intervalRef.current = getIntervalData();
    }
  }, [selectedGroups.length]);

  const columns = [
    {
      dataField: 'groupName',
      text: 'Group',
      sort: true,
      formatter: (cell, row) => formatGroupName(cell, row),
    },
    {
      dataField: 'numWarnings',
      text: 'Warnings',
      sort: true,
      headerStyle: { width: 100, textAlign: 'center' },
      style: { textAlign: 'center' },
    },
    {
      dataField: 'numAlarms',
      text: 'Alarms',
      sort: true,
      headerStyle: { width: 100, textAlign: 'center' },
      style: { textAlign: 'center' },
    },
    {
      dataField: 'numErrors',
      text: 'Errors',
      sort: true,
      headerStyle: { width: 100, textAlign: 'center' },
      style: { textAlign: 'center' },
    },
    {
      dataField: 'numSensors',
      text: 'Sensors',
      sort: true,
      headerStyle: { width: 100, textAlign: 'center' },
      style: { textAlign: 'center' },
    },
    {
      dataField: 'numOverriden',
      text: 'Overridden',
      sort: true,
      headerStyle: { width: 100, textAlign: 'center' },
      style: { textAlign: 'center' },
    },
    {
      dataField: 'state',
      text: 'State',
      sort: true,
      headerStyle: { width: 90 },
      formatter: formatState,
      filterValue: formatState,
    },
    {
      dataField: 'maps',
      text: 'Maps',
      sort: true,
      headerStyle: { width: 100, textAlign: 'center' },
      style: { textAlign: 'center' },
      formatter: formatMaps,
    },
  ];
  const defaultSorted = [
    {
      dataField: 'groupName',
      order: 'asc',
    },
  ];
  const ctxMenuOptions = [
    [
      {
        name: 'View group',
        action: (row, rowIndex) => {
          history.push(`/?groupId=${row.groupId}`);
        },
      },
      {
        name: 'Set at rest',
        className: (row) => {
          return helper.checkPermission('API.Sensors.ModifyAtRestState') &&
            row &&
            !row.state
            ? ''
            : 'item-disabled';
        },
        action: (row, rowIndex) => {
          onSetSensorsAtRest(row.groupId);
        },
      },
      {
        name: 'Set in operation',
        className: (row) =>
          helper.checkPermission('API.Sensors.ModifyAtRestState') &&
          row &&
          row.state
            ? ''
            : 'item-disabled',
        action: (row, rowIndex) => {
          onSetSensorsInOp(row.groupId);
        },
      },
      {
        name: 'Edit group',
        className: (row) =>
          helper.checkPermission('API.GroupSetting') ? '' : 'item-disabled',
        action: (row, rowIndex) => {
          history.push(`/configure/groups/edit-group/${row.groupId}`);
        },
      },
      {
        name: 'Maintain database',
        className: (row) =>
          helper.checkPermission('API.GroupSetting') ? '' : 'item-disabled',
        action: (row, rowIndex) => {
          history.push(`/configure/groups/maintain-db/${row.groupId}`);
        },
      },
    ],
  ];
  function rowStyle(row) {
    const style = {};
    style.backgroundColor = row.color && row.color.toLowerCase();
    return style;
  }
  const [isDisabling, setIsDisabling] = React.useState(false);
  const [isEnabling, setIsEnabling] = React.useState(false);

  function formatGroupName(cell, { groupId, groupName, ...row }) {
    const params = {
      groupId,
      groupName,
      state: row.state ? 'At rest' : 'In operation',
    };
    return (
      <a
        className="group-name"
        onClick={() => {
          history.push(`/custom-dashboard${helper.compileQueryString(params)}`);
        }}
      >
        {cell}
      </a>
    );
  }
  function formatState(cell) {
    if (cell) {
      return t('At rest');
    }
    return t('In operation');
  }
  function formatMaps(cell, row) {
    const Wrapper = ({ children, ...props }) => (
      <div
        style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
        {...props}
      >
        {children}
      </div>
    );
    if (cell) {
      return (
        <Wrapper
          onClick={() =>
            history.push(
              `/group-map?mapName=${cell}&mapDepartmentId=${row.mapDepartmentId}&mapGroupId=${row.groupId}`,
            )
          }
          title={cell}
        >
          <i className="md-icon">explore</i>
        </Wrapper>
      );
    }
    return cell;
  }
  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedGroups([...selectedGroups, row.groupId]);
    } else {
      setSelectedGroups(selectedGroups.filter((x) => x !== row.groupId));
    }
  };

  const handleOnSelectAll = (isSelect, rows) => {
    const ids = rows.map((r) => r.groupId);
    if (isSelect) {
      setSelectedGroups((selectedGroups) => [
        ...selectedGroups,
        ...ids.filter((id) => !selectedGroups.includes(id)),
      ]);
    } else {
      setSelectedGroups((selectedGroups) =>
        selectedGroups.filter((groupId) => !ids.includes(groupId)),
      );
    }
  };
  const selectRow = {
    mode: 'checkbox',
    // clickToSelect: true,
    selected: selectedGroups,
    onSelect: handleOnSelect,
    onSelectAll: handleOnSelectAll,
  };

  const actions = [
    {
      label: 'At rest',
      action: () => onSetSensorsAtRest(selectedGroups.join(',')),
      disabled: !(
        selectedGroups.length &&
        helper.checkPermission('API.Sensors.ModifyAtRestState')
      ),
    },
    {
      label: 'In operation',
      action: () => onSetSensorsInOp(selectedGroups.join(',')),
      disabled: !(
        selectedGroups.length &&
        helper.checkPermission('API.Sensors.ModifyAtRestState')
      ),
    },
    {
      label: 'Sensor disable',
      action: () => setIsDisabling(true),
      disabled: !(
        selectedGroups.length && helper.checkPermission('API.Sensors.Disable')
      ),
    },
    {
      label: 'Sensor enable',
      action: () => setIsEnabling(true),
      disabled: !(
        selectedGroups.length && helper.checkPermission('API.Sensors.Enable')
      ),
    },
  ];
  function onSetSensorsAtRest(groupIds) {
    setSensorsAtRest({ groupIds }).then(() => getData());
  }
  function onSetSensorsInOp(groupIds) {
    setSensorsInOp({ groupIds }).then(() => getData());
  }
  function onCancelModal(type = 'disable') {
    if (type === 'disable') {
      setIsDisabling(false);
    } else {
      setIsEnabling(false);
    }
    setSelectedGroups([]);
  }
  function onDisableSensor(params) {
    let groupIds = selectedGroups.join(',');
    setIsDisabling(false);
    setSensorsDisable({ groupIds, ...params }, 'group').then(() => getData());
  }
  function onEnableSensor(dateTime) {
    let groupIds = selectedGroups.join(',');
    setIsEnabling(false);
    setSensorsEnable({ groupIds }, 'group').then(() => getData());
  }
  return (
    <>
      <DisableModal
        isOpen={isDisabling}
        onCancel={() => onCancelModal('disable')}
        onConfirm={onDisableSensor}
      />
      <EnableModal
        isOpen={isEnabling}
        onCancel={() => onCancelModal('enable')}
        onConfirm={onEnableSensor}
      />
      <div>
        <div className="d-flex justify-content-between align-items-end mb-10">
          <div>
            <h1>{props.title}</h1>
            <span style={{ fontStyle: 'italic' }}>{props.description}</span>
          </div>
          <div className="d-flex flex-1 justify-content-end align-items-end">
            <ActionsDropdown options={actions} />
          </div>
        </div>
        <Datagrid
          datagridProps={{
            keyField: 'groupId',
            data: availableGroups,
            columns,
            defaultSorted,
            rowStyle,
            selectRow,
          }}
          classes=""
          ctxMenuId="AvailableGroups-context"
          ctxMenuOptions={ctxMenuOptions}
          exportProps={{
            fileName: `AlarmList-${Moment().format('MM-DD-YYYY')}.csv`,
          }}
        />
      </div>
    </>
  );
};

export default AGComponent;
